import { Box, Typography, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AddCircle as AddIcon,
  RemoveCircle as RemoveIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { isFieldDiff } from "../../types/review";

/**
 * Old→new rendering for a car wash's photo gallery.
 *
 * Images are the one field with no natural textual diff: the value is a list,
 * and "₼10 → ₼15" has no equivalent. So the gallery itself carries the
 * colour-coding — a kept photo renders plain, an added one green, a removed one
 * red and faded — which is far more readable than a URL comparison.
 *
 * Accepts both shapes the backend produces: an edit to an existing car wash
 * records {old, new}, while a brand-new submission has no "old" and stores a
 * plain array. In the second case every photo is new, so nothing is marked —
 * flagging all of them as "added" would be noise on a creation request.
 */

type ImagesValue = string[] | { old: string[]; new: string[] } | undefined;

type Tone = "kept" | "added" | "removed";

const TONE_STYLES: Record<
  Tone,
  { border: string; bg: (c: string) => string; opacity: number }
> = {
  kept: { border: "divider", bg: () => "transparent", opacity: 1 },
  added: { border: "success.main", bg: () => "success.main", opacity: 1 },
  removed: { border: "error.main", bg: () => "error.main", opacity: 0.45 },
};

const Thumb = ({
  src,
  tone,
  index,
}: {
  src: string;
  tone: Tone;
  index: number;
}) => {
  const { t } = useTranslation();
  const style = TONE_STYLES[tone];

  return (
    <Box sx={{ position: "relative", flexShrink: 0 }}>
      <Box
        component="img"
        src={src}
        alt={t("carWashes.imageAltIndexed", { index: index + 1 })}
        sx={{
          width: 96,
          height: 96,
          objectFit: "cover",
          borderRadius: 1,
          display: "block",
          border: "2px solid",
          borderColor: style.border,
          opacity: style.opacity,
          // A removed photo is also desaturated, so the state survives for a
          // reviewer who cannot distinguish the border colours.
          filter: tone === "removed" ? "grayscale(1)" : "none",
        }}
      />
      {tone !== "kept" && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            width: 20,
            height: 20,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "common.white",
            bgcolor: tone === "added" ? "success.main" : "error.main",
          }}
        >
          {tone === "added" ? (
            <AddIcon sx={{ fontSize: 14 }} />
          ) : (
            <RemoveIcon sx={{ fontSize: 14 }} />
          )}
        </Box>
      )}
    </Box>
  );
};

const Group = ({
  labelKey,
  count,
  tone,
  images,
  offset,
}: {
  labelKey: string;
  count: number;
  tone: Tone;
  images: string[];
  offset: number;
}) => {
  const { t } = useTranslation();
  if (images.length === 0) return null;

  return (
    <Box mt={1.5}>
      <Chip
        size="small"
        label={`${t(labelKey)} (${count})`}
        sx={{
          mb: 1,
          fontWeight: 600,
          ...(tone === "added" && {
            color: "success.dark",
            bgcolor: (theme: any) => alpha(theme.palette.success.main, 0.12),
          }),
          ...(tone === "removed" && {
            color: "error.dark",
            bgcolor: (theme: any) => alpha(theme.palette.error.main, 0.12),
          }),
        }}
      />
      <Box display="flex" gap={1} flexWrap="wrap">
        {images.map((src, i) => (
          <Thumb key={src} src={src} tone={tone} index={offset + i} />
        ))}
      </Box>
    </Box>
  );
};

const ImageDiff = ({ images }: { images: ImagesValue }) => {
  const { t } = useTranslation();

  if (!images) return null;

  // Creation request: a bare array, no previous state to compare against.
  if (Array.isArray(images)) {
    if (images.length === 0) return null;
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t("carWashDetails.images")} ({images.length})
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          {images.map((src, i) => (
            <Thumb key={src} src={src} tone="kept" index={i} />
          ))}
        </Box>
      </Box>
    );
  }

  if (!isFieldDiff<string[]>(images)) return null;

  const oldList = images.old ?? [];
  const newList = images.new ?? [];

  const kept = newList.filter((src) => oldList.includes(src));
  const added = newList.filter((src) => !oldList.includes(src));
  const removed = oldList.filter((src) => !newList.includes(src));

  if (kept.length === 0 && added.length === 0 && removed.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {t("carWashDetails.images")}
      </Typography>

      <Group
        labelKey="diff.imagesKept"
        count={kept.length}
        tone="kept"
        images={kept}
        offset={0}
      />
      <Group
        labelKey="diff.imagesAdded"
        count={added.length}
        tone="added"
        images={added}
        offset={kept.length}
      />
      <Group
        labelKey="diff.imagesRemoved"
        count={removed.length}
        tone="removed"
        images={removed}
        offset={kept.length + added.length}
      />
    </Box>
  );
};

export default ImageDiff;
